/* eslint-disable @typescript-eslint/no-explicit-any */
import { Divider } from "@/components/custom/divider";
import { Typography } from "@/components/custom/typography";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
// store removed; rely on server counts
import { NotificationContent } from "./NotificationContent";
import {
  TabValue,
  useNotificationDataInfinite,
} from "@/hooks/notification-hooks/useNotificationData";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

const tabListInfo = [
  { value: "all", content: "All" },
  {
    value: "contractAndOperationManagement",
    content: "ContractAndOperationManagement",
  },
  {
    value: "serviceUsageDetail",
    content: "ServiceUsageDetail",
  },
  { value: "announcement", content: "Announcement" },
] as const;

export function NotificationList() {
  const [tab, setTab] = useState<TabValue>("all");
  const router = useRouter();
  // total unread will come from unreadCounts (server-preferred)
  const t = useTranslations("notification");

  const { list, unreadCounts, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useNotificationDataInfinite(tab);

  const hasMarkedRef = useRef(false);

  // Total unread shown in the header badge (use server-provided value)
  const totalUnread = unreadCounts?.all ?? 0;

  // When the list loads, mark visible unread notifications as seen once.
  useEffect(() => {
    if (hasMarkedRef.current) return;
    const unreadIds = list.filter((n: any) => !n.read_at).map((n: any) => n.id);
    if (unreadIds.length === 0) return;
    hasMarkedRef.current = true;
  }, [list]);

  // === IntersectionObserver cho ScrollArea viewport ===
  const rootRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const rootEl = rootRef.current;
    const viewport = rootEl?.querySelector(
      "[data-radix-scroll-area-viewport]",
    ) as HTMLElement | null;
    const sentinel = sentinelRef.current;
    if (!viewport || !sentinel) return;

    const io = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;
        if (hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: viewport,
        rootMargin: "200px",
        threshold: 0,
      },
    );

    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, tab]);

  const tabTriggerStyle = (value: string) =>
    `relative px-4 py-2 !text-base !bg-white cursor-pointer duration-200  ${
      tab === (value as TabValue)
        ? "!text-black font-semibold data-[state=active]:text-black"
        : "text-muted-foreground"
    }`;

  const tabLineStyle = (value: string) =>
    `absolute -bottom-[1px] left-0 h-[3px] !text-black ${
      tab === (value as TabValue) ? "bg-blue-600" : "bg-secondary"
    }`;

  return (
    <>
      {/* Title */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Typography as={"h2"}>{t("title")}</Typography>
          {/* show badge only when there's at least one unread */}
          {totalUnread > 0 && (
            <Typography
              as={"span"}
              className="bg-red-500 text-white px-2 rounded-full"
              aria-label={`${totalUnread} unread notifications`}
            >
              {totalUnread > 99 ? "99+" : totalUnread}
            </Typography>
          )}
        </div>
        <Typography as={"span"} className="text-xs text-gray-500">
          {t("header.markAllRead")}
        </Typography>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as TabValue)}>
        <TabsList className="flex flex-nowrap gap-1 px-0 bg-transparent border-b-2 w-full">
          {tabListInfo.map((item) => {
            const count =
              item.value === "all"
                ? unreadCounts.all
                : (unreadCounts as any)[item.content];
            const label = t(`tabs.${item.value}`);
            return (
              <TabsTrigger
                key={item.value}
                value={item.value as TabValue}
                className={`${tabTriggerStyle(item.value)} flex-1 p-0`}
              >
                <span className="whitespace-nowrap truncate  flex">
                  {`${label}(${count || 0})`}
                </span>

                <div className={tabLineStyle(item.value)} />
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Content: infinite scroll */}
        <TabsContent value={tab} className="mt-2">
          <ScrollArea ref={rootRef} className="h-96 pr-4">
            {list.map((n: any) => (
              <div key={n.id}>
                <NotificationContent content={n} titleToShow={n.title!} />
                <Divider />
              </div>
            ))}
            <div ref={sentinelRef} className="h-6" />
            {isFetchingNextPage && (
              <div className="py-2 text-center text-xs text-gray-500">
                {t("header.loadingMore")}
              </div>
            )}
          </ScrollArea>

          <div className="flex justify-between items-center mt-2">
            <Typography
              as={"span"}
              className="underline underline-offset-2 text-gray-600 text-xs cursor-pointer"
              onClick={() => router.push("/dashboard/notifications")}
            >
              {tab === "announcement"
                ? t("announcementLink.allAnnouncements")
                : ""}
            </Typography>
            <Typography as={"span"} className="text-gray-600 text-2xs">
              {tab === "announcement"
                ? t("announcementLink.range6Months")
                : t("announcementLink.range15Days")}
            </Typography>
          </div>
        </TabsContent>
      </Tabs>
    </>
  );
}
